<template>
  <div id="mission-onelakh">
    <!-- Ambient Canvas Glow & Grid Pattern -->
    <div class="dashboard-canvas-decor" aria-hidden="true">
      <span class="decor-orb orb-blue" />
      <span class="decor-orb orb-gold" />
      <span class="decor-grid-pattern" />
    </div>

    <!-- Loading Overlay -->
    <v-overlay :value="LoadingFlag" z-index="999">
      <v-progress-circular indeterminate size="64" color="#0097d7" />
    </v-overlay>

    <!-- Compact Executive Header Section -->
    <div class="executive-header-banner">
      <div class="header-light-sweep" aria-hidden="true" />
      <div class="header-inner">
        <div class="header-left">
          <div class="trophy-emblem-box">
            <v-icon size="26" color="white">mdi-trophy-outline</v-icon>
          </div>
          <div class="header-titles">
            <h1 class="page-title-text">{{ PageTitle }}</h1>
            <div class="location-badge">
              <v-icon size="14" color="rgba(255,255,255,0.85)" class="mr-1">
                mdi-map-marker-outline
              </v-icon>
              <span>{{ rows.ZoneName }} - {{ rows.LomName }}</span>
            </div>
          </div>
        </div>

        <div class="header-right">
          <v-btn
            medium
            rounded
            elevation="0"
            class="refresh-action-btn"
            @click.prevent="refreshPageData"
          >
            <v-icon left class="refresh-icon">mdi-refresh</v-icon>
            <span>Refresh</span>
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Unified Phase Cards Grid -->
    <div class="phase-dashboard-grid">
      <!-- PHASE 1: Step 1 -->
      <div
        v-if="rows.Phase1Flag"
        class="phase-card-wrapper"
        style="--card-index: 1"
      >
        <div class="phase-card phase-1">
          <v-icon class="watermark-bg-icon" aria-hidden="true">
            mdi-account-group
          </v-icon>
          <div class="card-top-shine" aria-hidden="true" />
          <div class="card-corner-accent corner-tl" aria-hidden="true" />
          <div class="card-corner-accent corner-br" aria-hidden="true" />

          <div class="card-header-bar">
            <div class="step-badge">
              <span class="step-label">STEP</span>
              <v-icon size="22" color="white" class="step-num-icon">
                mdi-numeric-1-circle
              </v-icon>
            </div>

            <div v-if="rows.Phase1IconPath != ''" class="achievement-medallion">
              <div class="badge-orbit" aria-hidden="true">
                <span class="orbit-dot dot-1" />
                <span class="orbit-dot dot-2" />
              </div>
              <div class="badge-ring" aria-hidden="true" />
              <div class="badge-glow" aria-hidden="true" />
              <div class="badge-img-wrapper">
                <img
                  v-if="rows.Phase1IconPath"
                  :src="rows.Phase1IconPath"
                  alt="Achievement Badge"
                  class="badge-img"
                />
              </div>
            </div>
          </div>

          <div class="card-hero-row">
            <div class="hero-icon-container icon-animate-pulse">
              <div class="icon-halo" aria-hidden="true" />
              <v-icon size="28" color="white">mdi-account-group</v-icon>
            </div>
            <div class="title-wrap">
              <h2 class="phase-title-text">The Visionary Four</h2>
              <div class="title-accent-line" aria-hidden="true" />
            </div>
          </div>

          <div class="info-glass-panel">
            <div class="meta-row status-meta-row">
              <div class="meta-left">
                <span class="meta-label">Status</span>
                <v-chip
                  :color="getActiveStatusColor(rows.Phase1StatusTxt)"
                  draggable
                  dark
                  small
                  class="status-chip-pill"
                >
                  <span class="status-dot" aria-hidden="true" />
                  {{ rows.Phase1StatusTxt }}
                </v-chip>
              </div>

              <v-tooltip v-if="rows.Phase1CertificatePath != ''" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    :href="rows.Phase1CertificatePath"
                    target="_blank"
                    icon
                    dark
                    small
                    class="download-btn-icon"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon size="18" color="white">mdi-file-download</v-icon>
                  </v-btn>
                </template>
                <span>Download</span>
              </v-tooltip>
            </div>

            <div
              v-if="rows.Phase1DeadlineDateTxt"
              class="meta-row deadline-meta-row"
            >
              <span class="meta-label">
                <v-icon size="12" class="mr-1 opacity-70"
                  >mdi-calendar-clock</v-icon
                >
                DeadLine Date
              </span>
              <span class="meta-val">{{ rows.Phase1DeadlineDateTxt }}</span>
            </div>

            <div
              v-if="rows.Phase1CompletedDateTxt && false"
              class="meta-row completed-meta-row"
            >
              <span class="meta-label">
                <v-icon size="12" class="mr-1 opacity-70"
                  >mdi-calendar-check</v-icon
                >
                Completed Date
              </span>
              <span class="meta-val">{{ rows.Phase1CompletedDateTxt }}</span>
            </div>
          </div>

          <div class="card-action-bar">
            <router-link
              to="/mission-one-lakh/committee/add-member"
              class="action-link"
            >
              <v-btn
                id="add-member"
                rounded
                class="card-action-btn primary-action"
              >
                <span>Enter</span>
                <v-icon right small class="action-btn-arrow"
                  >mdi-arrow-right</v-icon
                >
              </v-btn>
            </router-link>

            <router-link
              v-if="!rows.Phase1Flag"
              to="/mission-one-lakh/committee/add-member"
              class="action-link ml-2"
            >
              <v-btn rounded class="card-action-btn secondary-action">
                <span>Start</span>
                <v-icon right small class="action-btn-arrow">mdi-play</v-icon>
              </v-btn>
            </router-link>
          </div>
        </div>
      </div>

      <!-- PHASE 2: Step 2 -->
      <div class="phase-card-wrapper" style="--card-index: 2">
        <div class="phase-card phase-2">
          <v-icon class="watermark-bg-icon" aria-hidden="true">
            mdi-target
          </v-icon>
          <div class="card-top-shine" aria-hidden="true" />
          <div class="card-corner-accent corner-tl" aria-hidden="true" />
          <div class="card-corner-accent corner-br" aria-hidden="true" />

          <div class="card-header-bar">
            <div class="step-badge">
              <span class="step-label">STEP</span>
              <v-icon size="22" color="white" class="step-num-icon">
                mdi-numeric-2-circle
              </v-icon>
            </div>

            <div v-if="rows.Phase2IconPath != ''" class="achievement-medallion">
              <div class="badge-orbit" aria-hidden="true">
                <span class="orbit-dot dot-1" />
                <span class="orbit-dot dot-2" />
              </div>
              <div class="badge-ring" aria-hidden="true" />
              <div class="badge-glow" aria-hidden="true" />
              <div class="badge-img-wrapper">
                <img
                  v-if="rows.Phase2IconPath"
                  :src="rows.Phase2IconPath"
                  alt="Achievement Badge"
                  class="badge-img"
                />
              </div>
            </div>
          </div>

          <div class="card-hero-row">
            <div class="hero-icon-container icon-animate-spin">
              <div class="icon-halo" aria-hidden="true" />
              <v-icon size="28" color="white">mdi-target</v-icon>
            </div>
            <div class="title-wrap">
              <h2 class="phase-title-text">The Power Prospect List</h2>
              <div class="title-accent-line" aria-hidden="true" />
            </div>
          </div>

          <div class="info-glass-panel">
            <div class="meta-row status-meta-row">
              <div class="meta-left">
                <span class="meta-label">Status</span>
                <v-chip
                  :color="getActiveStatusColor(rows.Phase2StatusTxt)"
                  draggable
                  dark
                  small
                  class="status-chip-pill"
                >
                  <span class="status-dot" aria-hidden="true" />
                  {{ rows.Phase2StatusTxt }}
                </v-chip>
              </div>

              <v-tooltip v-if="rows.Phase2CertificatePath != ''" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    :href="rows.Phase2CertificatePath"
                    target="_blank"
                    icon
                    dark
                    small
                    class="download-btn-icon"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon size="18" color="white">mdi-file-download</v-icon>
                  </v-btn>
                </template>
                <span>Download</span>
              </v-tooltip>
            </div>

            <div
              v-if="rows.Phase2DeadlineDateTxt"
              class="meta-row deadline-meta-row"
            >
              <span class="meta-label">
                <v-icon size="12" color="white" class="mr-1 opacity-70"
                  >mdi-calendar-clock</v-icon
                >
                DeadLine Date
              </span>
              <span class="meta-val">{{ rows.Phase2DeadlineDateTxt }}</span>
            </div>

            <div
              v-if="rows.Phase2CompletedDateTxt && false"
              class="meta-row completed-meta-row"
            >
              <span class="meta-label">
                <v-icon size="12" color="white" class="mr-1 opacity-70"
                  >mdi-calendar-check</v-icon
                >
                Completed Date
              </span>
              <span class="meta-val">{{ rows.Phase2CompletedDateTxt }}</span>
            </div>
          </div>

          <div class="card-action-bar">
            <router-link to="/mission-one-lakh/member-list" class="action-link">
              <v-btn
                id="add-member"
                rounded
                class="card-action-btn primary-action"
              >
                <span>Enter</span>
                <v-icon right small class="action-btn-arrow"
                  >mdi-arrow-right</v-icon
                >
              </v-btn>
            </router-link>

            <router-link
              v-if="!rows.Phase2Flag"
              to="/mission-one-lakh/member-list"
              class="action-link ml-2"
            >
              <v-btn rounded class="card-action-btn secondary-action">
                <span>Start</span>
                <v-icon right small class="action-btn-arrow">mdi-play</v-icon>
              </v-btn>
            </router-link>
          </div>
        </div>
      </div>

      <!-- PHASE 3: Step 3 -->
      <div
        v-if="rows.Phase3Flag"
        class="phase-card-wrapper"
        style="--card-index: 3"
      >
        <div class="phase-card phase-3">
          <v-icon class="watermark-bg-icon" aria-hidden="true">
            mdi-school
          </v-icon>
          <div class="card-top-shine" aria-hidden="true" />
          <div class="card-corner-accent corner-tl" aria-hidden="true" />
          <div class="card-corner-accent corner-br" aria-hidden="true" />

          <div class="card-header-bar">
            <div class="step-badge">
              <span class="step-label">STEP</span>
              <v-icon size="22" color="white" class="step-num-icon">
                mdi-numeric-3-circle
              </v-icon>
            </div>

            <div v-if="rows.Phase3IconPath != ''" class="achievement-medallion">
              <div class="badge-orbit" aria-hidden="true">
                <span class="orbit-dot dot-1" />
                <span class="orbit-dot dot-2" />
              </div>
              <div class="badge-ring" aria-hidden="true" />
              <div class="badge-glow" aria-hidden="true" />
              <div class="badge-img-wrapper">
                <img
                  v-if="rows.Phase3IconPath"
                  :src="rows.Phase3IconPath"
                  alt="Achievement Badge"
                  class="badge-img"
                />
              </div>
            </div>
          </div>

          <div class="card-hero-row">
            <div class="hero-icon-container icon-animate-float">
              <div class="icon-halo" aria-hidden="true" />
              <v-icon size="28" color="white">mdi-school</v-icon>
            </div>
            <div class="title-wrap">
              <h2 class="phase-title-text">
                Potential Members visited / invited
              </h2>
              <div class="title-accent-line" aria-hidden="true" />
            </div>
          </div>

          <div class="info-glass-panel">
            <div class="meta-row status-meta-row">
              <div class="meta-left">
                <span class="meta-label">Status</span>
                <v-chip
                  :color="getActiveStatusColor(rows.Phase3StatusTxt)"
                  draggable
                  dark
                  small
                  class="status-chip-pill"
                >
                  <span class="status-dot" aria-hidden="true" />
                  {{ rows.Phase3StatusTxt }}
                </v-chip>
              </div>

              <v-tooltip v-if="rows.Phase3CertificatePath != ''" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    :href="rows.Phase3CertificatePath"
                    target="_blank"
                    icon
                    dark
                    small
                    class="download-btn-icon"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon size="18" color="white">mdi-file-download</v-icon>
                  </v-btn>
                </template>
                <span>Download</span>
              </v-tooltip>
            </div>

            <div
              v-if="rows.Phase3DeadlineDateTxt"
              class="meta-row deadline-meta-row"
            >
              <span class="meta-label">
                <v-icon size="12" class="mr-1 opacity-70"
                  >mdi-calendar-clock</v-icon
                >
                DeadLine Date
              </span>
              <span class="meta-val">{{ rows.Phase3DeadlineDateTxt }}</span>
            </div>

            <div
              v-if="rows.Phase3CompletedDateTxt"
              class="meta-row completed-meta-row"
            >
              <span class="meta-label">
                <v-icon size="12" class="mr-1 opacity-70"
                  >mdi-calendar-check</v-icon
                >
                Completed Date
              </span>
              <span class="meta-val">{{ rows.Phase3CompletedDateTxt }}</span>
            </div>
          </div>

          <div class="card-action-bar">
            <router-link
              v-if="rows.Phase3ViewFlag"
              to="/mission-one-lakh/invited/member-list"
              class="action-link"
            >
              <v-btn
                id="add-member1"
                rounded
                class="card-action-btn primary-action"
              >
                <span>View</span>
                <v-icon right small class="action-btn-arrow"
                  >mdi-eye-outline</v-icon
                >
              </v-btn>
            </router-link>

            <router-link
              v-if="!rows.Phase3Flag"
              to="/mission-one-lakh/invited/member-list"
              class="action-link ml-2"
            >
              <v-btn rounded class="card-action-btn secondary-action">
                <span>Start</span>
                <v-icon right small class="action-btn-arrow">mdi-play</v-icon>
              </v-btn>
            </router-link>
          </div>
        </div>
      </div>

      <!-- PHASE 4: Step 4 -->
      <div v-if="CardFlag4" class="phase-card-wrapper" style="--card-index: 4">
        <div class="phase-card phase-4">
          <v-icon class="watermark-bg-icon" aria-hidden="true">
            mdi-star-outline
          </v-icon>
          <div class="card-top-shine" aria-hidden="true" />
          <div class="card-corner-accent corner-tl" aria-hidden="true" />
          <div class="card-corner-accent corner-br" aria-hidden="true" />

          <div class="card-header-bar">
            <div class="step-badge">
              <span class="step-label">STEP</span>
              <v-icon size="22" color="white" class="step-num-icon">
                mdi-numeric-4-circle
              </v-icon>
            </div>

            <div v-if="rows.Phase4IconPath != ''" class="achievement-medallion">
              <div class="badge-orbit" aria-hidden="true">
                <span class="orbit-dot dot-1" />
                <span class="orbit-dot dot-2" />
              </div>
              <div class="badge-ring" aria-hidden="true" />
              <div class="badge-glow" aria-hidden="true" />
              <div class="badge-img-wrapper">
                <img
                  v-if="rows.Phase4IconPath"
                  :src="rows.Phase4IconPath"
                  alt="Achievement Badge"
                  class="badge-img"
                />
              </div>
            </div>
          </div>

          <div class="card-hero-row">
            <div class="hero-icon-container icon-animate-sparkle">
              <div class="icon-halo" aria-hidden="true" />
              <v-icon size="28" color="white">mdi-star-outline</v-icon>
            </div>
            <div class="title-wrap">
              <h2 class="phase-title-text">
                Potential Members re-visited / re-invited
              </h2>
              <div class="title-accent-line" aria-hidden="true" />
            </div>
          </div>

          <div class="info-glass-panel">
            <div class="meta-row status-meta-row">
              <div class="meta-left">
                <span class="meta-label">Status</span>
                <v-chip
                  :color="getActiveStatusColor(rows.Phase4StatusTxt)"
                  draggable
                  dark
                  small
                  class="status-chip-pill"
                >
                  <span class="status-dot" aria-hidden="true" />
                  {{ rows.Phase4StatusTxt }}
                </v-chip>
              </div>

              <v-tooltip v-if="rows.Phase4CertificatePath != ''" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    :href="rows.Phase4CertificatePath"
                    target="_blank"
                    icon
                    dark
                    small
                    class="download-btn-icon"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon size="18" color="white">mdi-file-download</v-icon>
                  </v-btn>
                </template>
                <span>Download</span>
              </v-tooltip>
            </div>

            <div
              v-if="rows.Phase4DeadlineDateTxt"
              class="meta-row deadline-meta-row"
            >
              <span class="meta-label">
                <v-icon size="12" class="mr-1 opacity-70"
                  >mdi-calendar-clock</v-icon
                >
                DeadLine Date
              </span>
              <span class="meta-val">{{ rows.Phase4DeadlineDateTxt }}</span>
            </div>

            <div
              v-if="rows.Phase4CompletedDateTxt"
              class="meta-row completed-meta-row"
            >
              <span class="meta-label">
                <v-icon size="12" class="mr-1 opacity-70"
                  >mdi-calendar-check</v-icon
                >
                Completed Date
              </span>
              <span class="meta-val">{{ rows.Phase4CompletedDateTxt }}</span>
            </div>
          </div>

          <div class="card-action-bar">
            <router-link
              v-if="rows.Phase4ViewFlag"
              to="/mission-one-lakh/re-invited/member-list"
              class="action-link"
            >
              <v-btn
                id="add-member1"
                rounded
                class="card-action-btn primary-action"
              >
                <span>View</span>
                <v-icon right small class="action-btn-arrow"
                  >mdi-eye-outline</v-icon
                >
              </v-btn>
            </router-link>

            <router-link
              v-if="!rows.Phase4Flag"
              to="/mission-one-lakh/re-invited/member-list"
              class="action-link ml-2"
            >
              <v-btn rounded class="card-action-btn secondary-action">
                <span>Start</span>
                <v-icon right small class="action-btn-arrow">mdi-play</v-icon>
              </v-btn>
            </router-link>
          </div>
        </div>
      </div>

      <!-- PHASE 5: Step 5 -->
      <div class="phase-card-wrapper" style="--card-index: 5">
        <div class="phase-card phase-5">
          <v-icon class="watermark-bg-icon" aria-hidden="true">
            mdi-lightbulb-outline
          </v-icon>
          <div class="card-top-shine" aria-hidden="true" />
          <div class="card-corner-accent corner-tl" aria-hidden="true" />
          <div class="card-corner-accent corner-br" aria-hidden="true" />

          <div class="card-header-bar">
            <div class="step-badge">
              <span class="step-label">STEP</span>
              <v-icon size="22" color="white" class="step-num-icon">
                mdi-numeric-3-circle
              </v-icon>
            </div>

            <div v-if="rows.Phase5IconPath != ''" class="achievement-medallion">
              <div class="badge-orbit" aria-hidden="true">
                <span class="orbit-dot dot-1" />
                <span class="orbit-dot dot-2" />
              </div>
              <div class="badge-ring" aria-hidden="true" />
              <div class="badge-glow" aria-hidden="true" />
              <div class="badge-img-wrapper">
                <img
                  v-if="rows.Phase5IconPath"
                  :src="rows.Phase5IconPath"
                  alt="Achievement Badge"
                  class="badge-img"
                />
              </div>
            </div>
          </div>

          <div class="card-hero-row">
            <div class="hero-icon-container icon-animate-glow">
              <div class="icon-halo" aria-hidden="true" />
              <v-icon size="28" color="white">mdi-lightbulb-outline</v-icon>
            </div>
            <div class="title-wrap">
              <h2 class="phase-title-text">The Grand Orientation</h2>
              <div class="title-accent-line" aria-hidden="true" />
            </div>
          </div>

          <div class="info-glass-panel">
            <div class="meta-row status-meta-row">
              <div class="meta-left">
                <span class="meta-label">Status</span>
                <v-chip
                  :color="getActiveStatusColor(rows.Phase5StatusTxt)"
                  draggable
                  dark
                  small
                  class="status-chip-pill"
                >
                  <span class="status-dot" aria-hidden="true" />
                  {{ rows.Phase5StatusTxt }}
                </v-chip>
              </div>

              <v-tooltip v-if="rows.Phase5CertificatePath != ''" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    :href="rows.Phase5CertificatePath"
                    target="_blank"
                    icon
                    dark
                    small
                    class="download-btn-icon"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon size="18" color="white">mdi-file-download</v-icon>
                  </v-btn>
                </template>
                <span>Download</span>
              </v-tooltip>
            </div>

            <div
              v-if="rows.Phase5DeadlineDateTxt"
              class="meta-row deadline-meta-row"
            >
              <span class="meta-label">
                <v-icon size="12" class="mr-1 opacity-70"
                  >mdi-calendar-clock</v-icon
                >
                DeadLine Date
              </span>
              <span class="meta-val">{{ rows.Phase5DeadlineDateTxt }}</span>
            </div>

            <div
              v-if="rows.Phase5CompletedDateTxt && false"
              class="meta-row completed-meta-row"
            >
              <span class="meta-label">
                <v-icon size="12" class="mr-1 opacity-70"
                  >mdi-calendar-check</v-icon
                >
                Completed Date
              </span>
              <span class="meta-val">{{ rows.Phase5CompletedDateTxt }}</span>
            </div>
          </div>

          <div class="card-action-bar">
            <router-link
              v-if="rows.Phase5ViewFlag"
              to="/mission-one-lakh/attended/member-list"
              class="action-link"
            >
              <v-btn
                id="add-member1"
                rounded
                class="card-action-btn primary-action"
              >
                <span>View</span>
                <v-icon right small class="action-btn-arrow"
                  >mdi-eye-outline</v-icon
                >
              </v-btn>
            </router-link>

            <router-link
              v-if="!rows.Phase5Flag"
              to="/mission-one-lakh/attended/member-list"
              class="action-link ml-2"
            >
              <v-btn
                id="add-member1"
                rounded
                class="card-action-btn secondary-action"
              >
                <span>Start</span>
                <v-icon right small class="action-btn-arrow">mdi-play</v-icon>
              </v-btn>
            </router-link>
          </div>
        </div>
      </div>

      <!-- PHASE 6: Step 6 -->
      <div class="phase-card-wrapper" style="--card-index: 6">
        <div class="phase-card phase-6">
          <v-icon class="watermark-bg-icon" aria-hidden="true">
            mdi-star-outline
          </v-icon>
          <div class="card-top-shine" aria-hidden="true" />
          <div class="card-corner-accent corner-tl" aria-hidden="true" />
          <div class="card-corner-accent corner-br" aria-hidden="true" />

          <div class="card-header-bar">
            <div class="step-badge">
              <span class="step-label">STEP</span>
              <v-icon size="22" color="white" class="step-num-icon">
                mdi-numeric-4-circle
              </v-icon>
            </div>

            <div
              v-if="rows.Phase6StatusTxt === 'Completed'"
              class="achievement-medallion"
            >
              <div class="badge-orbit" aria-hidden="true">
                <span class="orbit-dot dot-1" />
                <span class="orbit-dot dot-2" />
              </div>
              <div class="badge-ring" aria-hidden="true" />
              <div class="badge-glow" aria-hidden="true" />
              <div class="badge-img-wrapper">
                <img
                  v-if="rows.Phase6IconPath"
                  :src="rows.Phase6IconPath"
                  alt="Achievement Badge"
                  class="badge-img"
                />
              </div>
            </div>
          </div>

          <div class="card-hero-row">
            <div class="hero-icon-container icon-animate-sparkle">
              <div class="icon-halo" aria-hidden="true" />
              <v-icon size="28" color="white">mdi-star-outline</v-icon>
            </div>
            <div class="title-wrap">
              <h2 class="phase-title-text">The Conversion Challenge</h2>
              <div class="title-accent-line" aria-hidden="true" />
            </div>
          </div>

          <div class="info-glass-panel">
            <div class="meta-row status-meta-row">
              <div class="meta-left">
                <span class="meta-label">Status</span>
                <v-chip
                  :color="getActiveStatusColor(rows.Phase6StatusTxt)"
                  draggable
                  dark
                  small
                  class="status-chip-pill"
                >
                  <span class="status-dot" aria-hidden="true" />
                  {{ rows.Phase6StatusTxt }}
                </v-chip>
              </div>

              <v-tooltip v-if="rows.Phase6CertificatePath != ''" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    :href="rows.Phase6CertificatePath"
                    target="_blank"
                    icon
                    dark
                    small
                    class="download-btn-icon"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon size="18" color="white">mdi-file-download</v-icon>
                  </v-btn>
                </template>
                <span>Download</span>
              </v-tooltip>
            </div>

            <div
              v-if="rows.Phase6DeadlineDateTxt"
              class="meta-row deadline-meta-row"
            >
              <span class="meta-label">
                <v-icon size="12" class="mr-1 opacity-70"
                  >mdi-calendar-clock</v-icon
                >
                DeadLine Date
              </span>
              <span class="meta-val">{{ rows.Phase6DeadlineDateTxt }}</span>
            </div>

            <div
              v-if="rows.Phase6CompletedDateTxt && false"
              class="meta-row completed-meta-row"
            >
              <span class="meta-label">
                <v-icon size="12" class="mr-1 opacity-70"
                  >mdi-calendar-check</v-icon
                >
                Completed Date
              </span>
              <span class="meta-val">{{ rows.Phase6CompletedDateTxt }}</span>
            </div>
          </div>

          <div class="card-action-bar">
            <router-link
              v-if="rows.Phase6ViewFlag"
              to="/mission-one-lakh/join-jci-member-list"
              class="action-link"
            >
              <v-btn rounded class="card-action-btn primary-action">
                <span>View</span>
                <v-icon right small class="action-btn-arrow"
                  >mdi-eye-outline</v-icon
                >
              </v-btn>
            </router-link>

            <router-link
              to="/mission-one-lakh/join-jci-member-list"
              class="action-link ml-2"
            >
              <v-btn
                id="add-member1"
                rounded
                class="card-action-btn secondary-action"
              >
                <span>Start</span>
                <v-icon right small class="action-btn-arrow">mdi-play</v-icon>
              </v-btn>
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";

export default {
  mixins: [common],
  components: {},
  data() {
    return {
      CardFlag1: true,
      CardFlag2: true,
      CardFlag3: true,
      CardFlag4: false,
      CardFlag5: true,
      CardFlag6: true,

      addRecordPrompt: false,
      LoadingFlag: false,
      PageInfo: {},
      rows: {
        Phase1Flag: true,
      },
      PageTitle: "",
      PageDescription: "",
      JciYearCode: "",
      YearName: "",
      NextYearCode: "",
      NextYearName: "",
    };
  },
  computed: {},
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });
        this.JciYearCode = CurrentJciYearId;

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        var NextJciYearId = parseInt(tr.NextJciYearId);
        console.log({ NextJciYearId });
        this.NextYearCode = NextJciYearId;

        var NextJciYearName = parseInt(tr.NextJciYearName);
        console.log({ NextJciYearName });
        this.NextYearName = NextJciYearName;

        this.CurrentYearId = NextJciYearId;

        var LomId = this.$session.get("LomId");
        LomId = LomId == (null || undefined) ? "" : LomId;
        this.LomCode = LomId;

        this.pageData();
      }
    },
    rows: function () {
      console.log("watch rows");
    },
  },
  methods: {
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "mission_one_lakh",
        Action: "dashboard",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
      this.getTableRecords();
    },
    pageData() {
      console.log("pageData called");
    },
    getTableRecords() {
      console.log("getTableRecords called");

      var YearCode = this.JciYearCode;
      console.log("YearCode=" + YearCode);

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/mol-dashboard";
      var upload = {
        UserInterface: 1,
      };
      console.log("upload=" + JSON.stringify(upload));
      console.log(
        "server_url=" + server_url + ", token=" + token + ", add_url=" + add_url
      );

      this.SearchFlag = true;
      this.LoadingFlag = true;
      this.OverlayLoadingFlag = true;

      const thisIns = this;
      var output = "";
      var records = "";
      var flag = 0;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        onUploadProgress: function (progressEvent) {
          this.uploadPercentage = parseInt(
            Math.round((progressEvent.loaded / progressEvent.total) * 100)
          );
        }.bind(this),
      })
        .then(function (response) {
          console.log("response=" + JSON.stringify(response));

          thisIns.SearchFlag = false;
          thisIns.LoadingFlag = false;
          thisIns.StartupLoadingFlag = false;
          thisIns.OverlayLoadingFlag = false;

          output = response.data.output;
          flag = response.data.flag;
          output = response.data.output;
          records = response.data.records;
          console.log(
            "output=" + output + ", flag=" + flag + ", records=" + records
          );

          if (flag == 1) {
            thisIns.rows = records;
            thisIns.toast("success", output);
          } else {
            thisIns.toast("error", output);
          }
        })
        .catch(function (error) {
          console.log("error=" + error);
          thisIns.toast("error", error);
          thisIns.SearchFlag = false;
          thisIns.LoadingFlag = false;
        });
    },
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>

<style lang="scss" scoped>
@import url("https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap");

#mission-onelakh {
  position: relative;
  min-height: 100vh;
  padding: 18px 24px 36px 24px;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  font-family: "Outfit", "Plus Jakarta Sans", sans-serif;
  box-sizing: border-box;
  overflow-x: hidden;

  /* Ambient Dashboard Canvas Decor */
  .dashboard-canvas-decor {
    position: absolute;
    inset: 0;
    pointer-events: none;
    overflow: hidden;
    z-index: 0;

    .decor-orb {
      position: absolute;
      border-radius: 50%;
      filter: blur(80px);
      opacity: 0.45;
      animation: ambientOrbFloat 20s ease-in-out infinite alternate;
    }

    .orb-blue {
      width: 480px;
      height: 480px;
      top: -100px;
      right: -50px;
      background: rgba(0, 151, 215, 0.12);
    }

    .orb-gold {
      width: 420px;
      height: 420px;
      bottom: -80px;
      left: -60px;
      background: rgba(255, 193, 7, 0.12);
    }

    .decor-grid-pattern {
      position: absolute;
      inset: 0;
      background-image: radial-gradient(
        rgba(15, 23, 42, 0.04) 1px,
        transparent 1px
      );
      background-size: 24px 24px;
      opacity: 0.7;
    }
  }

  /* Compact Executive Header */
  .executive-header-banner {
    position: relative;
    z-index: 1;
    width: 100%;
    max-width: 1560px;
    margin: 0 auto 20px auto;
    padding: 16px 24px;
    border-radius: 16px;
    background: linear-gradient(135deg, #0284c7 0%, #0097d7 50%, #0369a1 100%);
    box-shadow: 0 10px 28px rgba(0, 151, 215, 0.28),
      inset 0 1px 0 rgba(255, 255, 255, 0.25);
    overflow: hidden;

    .header-light-sweep {
      position: absolute;
      top: 0;
      left: -100%;
      width: 60%;
      height: 100%;
      background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.15),
        transparent
      );
      transform: skewX(-25deg);
      animation: headerShimmer 10s ease-in-out infinite;
    }

    .header-inner {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      position: relative;
      z-index: 2;
    }

    .header-left {
      display: flex;
      align-items: center;
      gap: 14px;
    }

    .trophy-emblem-box {
      width: 44px;
      height: 44px;
      border-radius: 12px;
      background: rgba(255, 255, 255, 0.2);
      backdrop-filter: blur(8px);
      border: 1px solid rgba(255, 255, 255, 0.35);
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: inset 0 1px 2px rgba(255, 255, 255, 0.3),
        0 4px 12px rgba(0, 0, 0, 0.12);
    }

    .header-titles {
      .page-title-text {
        font-size: 1.4rem;
        font-weight: 800;
        color: #ffffff;
        letter-spacing: -0.01em;
        margin: 0;
        line-height: 1.2;
        text-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
      }

      .location-badge {
        display: inline-flex;
        align-items: center;
        margin-top: 3px;
        font-size: 0.85rem;
        font-weight: 600;
        color: rgba(255, 255, 255, 0.92);
        letter-spacing: 0.01em;
      }
    }

    .refresh-action-btn {
      background: rgba(255, 255, 255, 0.2) !important;
      color: #ffffff !important;
      border: 1px solid rgba(255, 255, 255, 0.35) !important;
      backdrop-filter: blur(8px);
      font-weight: 700 !important;
      letter-spacing: 0.03em !important;
      transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1) !important;

      .refresh-icon {
        transition: transform 0.6s ease;
      }

      &:hover {
        background: rgba(255, 255, 255, 0.32) !important;
        box-shadow: 0 6px 16px rgba(0, 0, 0, 0.18) !important;

        .refresh-icon {
          transform: rotate(180deg);
        }
      }
    }
  }

  /* Unified Phase Cards Grid Architecture */
  .phase-dashboard-grid {
    position: relative;
    z-index: 1;
    width: 100%;
    max-width: 1560px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
  }

  .phase-card-wrapper {
    opacity: 0;
    animation: cardEntrance 0.55s cubic-bezier(0.16, 1, 0.3, 1) forwards;
    animation-delay: calc(var(--card-index, 1) * 75ms);
  }

  /* Base Phase Card Styling */
  .phase-card {
    position: relative;
    border-radius: 20px;
    padding: 18px 20px 20px 20px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 14px;
    overflow: hidden;
    box-sizing: border-box;
    transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1),
      box-shadow 0.4s cubic-bezier(0.16, 1, 0.3, 1), border-color 0.4s ease;

    /* Subtle corner geometry accents */
    .card-corner-accent {
      position: absolute;
      width: 12px;
      height: 12px;
      pointer-events: none;
      opacity: 0.35;
      z-index: 1;

      &.corner-tl {
        top: 8px;
        left: 8px;
        border-top: 1.5px solid rgba(255, 255, 255, 0.6);
        border-left: 1.5px solid rgba(255, 255, 255, 0.6);
      }

      &.corner-br {
        bottom: 8px;
        right: 8px;
        border-bottom: 1.5px solid rgba(255, 255, 255, 0.6);
        border-right: 1.5px solid rgba(255, 255, 255, 0.6);
      }
    }

    /* Watermark Icon */
    .watermark-bg-icon {
      position: absolute !important;
      right: -15px;
      bottom: -15px;
      font-size: 145px !important;
      color: rgba(255, 255, 255, 0.07) !important;
      pointer-events: none;
      z-index: 0;
      transform: rotate(-10deg);
      transition: transform 0.5s ease, opacity 0.5s ease;
    }

    /* Card Top Shimmer Effect */
    .card-top-shine {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 2px;
      background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.7),
        transparent
      );
      opacity: 0.7;
    }

    /* Hover Light Sweep */
    &::after {
      content: "";
      position: absolute;
      top: 0;
      left: -120%;
      width: 70%;
      height: 100%;
      background: linear-gradient(
        110deg,
        transparent,
        rgba(255, 255, 255, 0.14),
        transparent
      );
      transform: skewX(-25deg);
      pointer-events: none;
      transition: left 0.85s ease;
    }

    &:hover {
      transform: translateY(-5px) scale(1.008);

      &::after {
        left: 150%;
      }

      .watermark-bg-icon {
        transform: rotate(-5deg) scale(1.05);
        opacity: 0.12;
      }
    }
  }

  /* Phase Color Identities & Themes */
  .phase-1 {
    --phase-color: #0097d7;
    --phase-color-rgb: 0, 151, 215;
    background: linear-gradient(135deg, #094074 0%, #0076c5 50%, #00a6f3 100%);
    box-shadow: 0 12px 30px rgba(0, 118, 197, 0.32),
      inset 0 1px 0 rgba(255, 255, 255, 0.3);
    border: 1px solid rgba(255, 255, 255, 0.3);
    color: #ffffff;

    --glass-bg: rgba(255, 255, 255, 0.14);
    --glass-border: rgba(255, 255, 255, 0.22);
    --text-main: #ffffff;
    --text-muted: rgba(255, 255, 255, 0.85);

    &:hover {
      box-shadow: 0 18px 42px rgba(0, 166, 243, 0.45),
        inset 0 1px 0 rgba(255, 255, 255, 0.45);
      border-color: rgba(255, 255, 255, 0.5);
    }
  }

  .phase-2 {
    --phase-color: #eab308;
    --phase-color-rgb: 234, 179, 8;
    background: linear-gradient(135deg, #854d0e 0%, #ca8a04 50%, #eab308 100%);
    box-shadow: 0 12px 30px rgba(202, 138, 4, 0.35),
      inset 0 1px 0 rgba(255, 255, 255, 0.35);
    border: 1px solid rgba(255, 255, 255, 0.35);
    color: #ffffff;

    --glass-bg: rgba(255, 255, 255, 0.14);
    --glass-border: rgba(255, 255, 255, 0.22);
    --text-main: #ffffff;
    --text-muted: rgba(255, 255, 255, 0.85);

    .watermark-bg-icon {
      color: rgba(255, 255, 255, 0.08) !important;
    }

    &:hover {
      box-shadow: 0 18px 42px rgba(234, 179, 8, 0.45),
        inset 0 1px 0 rgba(255, 255, 255, 0.5);
      border-color: rgba(255, 255, 255, 0.5);
    }
  }

  .phase-3 {
    --phase-color: #10b981;
    --phase-color-rgb: 16, 185, 129;
    background: linear-gradient(135deg, #064e3b 0%, #059669 50%, #10b981 100%);
    box-shadow: 0 12px 30px rgba(5, 150, 105, 0.32),
      inset 0 1px 0 rgba(255, 255, 255, 0.3);
    border: 1px solid rgba(255, 255, 255, 0.3);
    color: #ffffff;

    --glass-bg: rgba(255, 255, 255, 0.14);
    --glass-border: rgba(255, 255, 255, 0.22);
    --text-main: #ffffff;
    --text-muted: rgba(255, 255, 255, 0.85);

    &:hover {
      box-shadow: 0 18px 42px rgba(16, 185, 129, 0.45),
        inset 0 1px 0 rgba(255, 255, 255, 0.45);
      border-color: rgba(255, 255, 255, 0.5);
    }
  }

  .phase-4 {
    --phase-color: #8b5cf6;
    --phase-color-rgb: 139, 92, 246;
    background: linear-gradient(135deg, #3b0764 0%, #6d28d9 50%, #8b5cf6 100%);
    box-shadow: 0 12px 30px rgba(109, 40, 217, 0.32),
      inset 0 1px 0 rgba(255, 255, 255, 0.3);
    border: 1px solid rgba(255, 255, 255, 0.3);
    color: #ffffff;

    --glass-bg: rgba(255, 255, 255, 0.14);
    --glass-border: rgba(255, 255, 255, 0.22);
    --text-main: #ffffff;
    --text-muted: rgba(255, 255, 255, 0.85);

    &:hover {
      box-shadow: 0 18px 42px rgba(139, 92, 246, 0.45),
        inset 0 1px 0 rgba(255, 255, 255, 0.45);
      border-color: rgba(255, 255, 255, 0.5);
    }
  }

  .phase-5 {
    --phase-color: #f97316;
    --phase-color-rgb: 249, 115, 22;
    background: linear-gradient(135deg, #9a3412 0%, #ea580c 50%, #f97316 100%);
    box-shadow: 0 12px 30px rgba(234, 88, 12, 0.35),
      inset 0 1px 0 rgba(255, 255, 255, 0.3);
    border: 1px solid rgba(255, 255, 255, 0.3);
    color: #ffffff;

    --glass-bg: rgba(255, 255, 255, 0.14);
    --glass-border: rgba(255, 255, 255, 0.22);
    --text-main: #ffffff;
    --text-muted: rgba(255, 255, 255, 0.85);

    &:hover {
      box-shadow: 0 18px 42px rgba(249, 115, 22, 0.45),
        inset 0 1px 0 rgba(255, 255, 255, 0.45);
      border-color: rgba(255, 255, 255, 0.5);
    }
  }

  .phase-6 {
    --phase-color: #ef4444;
    --phase-color-rgb: 239, 68, 68;
    background: linear-gradient(135deg, #7f1d1d 0%, #dc2626 50%, #ef4444 100%);
    box-shadow: 0 12px 30px rgba(220, 38, 38, 0.35),
      inset 0 1px 0 rgba(255, 255, 255, 0.3);
    border: 1px solid rgba(255, 255, 255, 0.3);
    color: #ffffff;

    --glass-bg: rgba(255, 255, 255, 0.14);
    --glass-border: rgba(255, 255, 255, 0.22);
    --text-main: #ffffff;
    --text-muted: rgba(255, 255, 255, 0.85);

    &:hover {
      box-shadow: 0 18px 42px rgba(239, 68, 68, 0.45),
        inset 0 1px 0 rgba(255, 255, 255, 0.45);
      border-color: rgba(255, 255, 255, 0.5);
    }
  }

  /* Card Header Bar */
  .card-header-bar {
    position: relative;
    z-index: 2;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;

    .step-badge {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 4px 10px;
      border-radius: 20px;
      background: rgba(255, 255, 255, 0.18);
      backdrop-filter: blur(6px);
      border: 1px solid rgba(255, 255, 255, 0.3);
      box-shadow: inset 0 1px 2px rgba(255, 255, 255, 0.25);

      .step-label {
        font-size: 0.72rem;
        font-weight: 800;
        letter-spacing: 0.08em;
        color: #ffffff;

        &.label-dark {
          color: #2c3e50;
        }
      }

      .step-num-icon {
        transition: transform 0.35s ease;
      }
    }
  }

  .phase-card:hover .step-badge .step-num-icon {
    transform: scale(1.1) rotate(6deg);
  }

  /* Prominent Achievement Medallion */
  .achievement-medallion {
    position: relative;
    width: 92px;
    height: 92px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    .badge-glow {
      position: absolute;
      inset: 2px;
      border-radius: 50%;
      background: radial-gradient(
        circle,
        rgba(255, 255, 255, 0.6) 0%,
        transparent 70%
      );
      filter: blur(12px);
      opacity: 0.6;
      transition: opacity 0.4s ease, transform 0.4s ease;
    }

    .badge-ring {
      position: absolute;
      inset: 0px;
      border-radius: 50%;
      border: 1.5px dashed rgba(255, 255, 255, 0.45);
      animation: ringRotate 22s linear infinite;
    }

    .badge-orbit {
      position: absolute;
      inset: -6px;
      border-radius: 50%;
      border: 1px solid rgba(255, 255, 255, 0.3);
      animation: ringRotate 14s linear infinite;

      .orbit-dot {
        position: absolute;
        width: 5px;
        height: 5px;
        border-radius: 50%;
        background: #ffffff;
        box-shadow: 0 0 6px #ffffff;
        top: -2.5px;
        left: 50%;
        transform: translateX(-50%);

        &.dot-2 {
          top: auto;
          bottom: -2.5px;
          background: rgba(255, 255, 255, 0.7);
        }
      }
    }

    .badge-img-wrapper {
      position: relative;
      z-index: 2;
      width: 76px;
      height: 76px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.22);
      backdrop-filter: blur(6px);
      border: 1.5px solid rgba(255, 255, 255, 0.4);
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 6px;
      box-shadow: inset 0 2px 4px rgba(255, 255, 255, 0.35),
        0 8px 20px rgba(0, 0, 0, 0.16);
      animation: badgeFloat 4s ease-in-out infinite alternate;

      .badge-img {
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
        filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.2));
        transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
      }
    }
  }

  .phase-card:hover {
    .achievement-medallion {
      .badge-glow {
        opacity: 0.95;
        transform: scale(1.12);
      }
      .badge-orbit {
        animation-duration: 7s;
        border-color: rgba(255, 255, 255, 0.6);
      }
      .badge-img-wrapper {
        border-color: rgba(255, 255, 255, 0.75);

        .badge-img {
          transform: scale(1.08) translateY(-2px);
        }
      }
    }
  }

  /* Hero Row (Main Icon + Title + Accent Line) */
  .card-hero-row {
    position: relative;
    z-index: 2;
    display: flex;
    align-items: center;
    gap: 14px;

    .hero-icon-container {
      position: relative;
      width: 48px;
      height: 48px;
      border-radius: 14px;
      background: rgba(255, 255, 255, 0.18);
      backdrop-filter: blur(6px);
      border: 1px solid rgba(255, 255, 255, 0.32);
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      box-shadow: inset 0 1px 2px rgba(255, 255, 255, 0.25),
        0 4px 12px rgba(0, 0, 0, 0.1);
      transition: transform 0.35s ease, background 0.35s ease,
        border-color 0.35s ease;

      &.icon-dark-bg {
        background: rgba(44, 62, 80, 0.14);
        border: 1px solid rgba(44, 62, 80, 0.25);
      }

      .icon-halo {
        position: absolute;
        inset: -6px;
        border-radius: 18px;
        background: radial-gradient(
          circle,
          rgba(255, 255, 255, 0.3) 0%,
          transparent 70%
        );
        pointer-events: none;
        opacity: 0.6;
      }
    }

    .title-wrap {
      flex: 1;

      .phase-title-text {
        font-size: 1.15rem;
        font-weight: 800;
        line-height: 1.25;
        letter-spacing: -0.01em;
        color: var(--text-main);
        margin: 0;
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);

        &.title-dark {
          color: #2c3e50;
          text-shadow: none;
        }
      }

      .title-accent-line {
        height: 2.5px;
        width: 32px;
        margin-top: 6px;
        background: linear-gradient(
          90deg,
          rgba(255, 255, 255, 0.85),
          transparent
        );
        border-radius: 2px;
        transition: width 0.4s cubic-bezier(0.16, 1, 0.3, 1);
      }
    }
  }

  .phase-2 .card-hero-row .title-wrap .title-accent-line {
    background: linear-gradient(90deg, rgba(255, 255, 255, 0.6), transparent);
  }

  .phase-card:hover {
    .hero-icon-container {
      transform: translateY(-3px) scale(1.08);
      background: rgba(255, 255, 255, 0.28);
      border-color: rgba(255, 255, 255, 0.5);

      &.icon-dark-bg {
        background: rgba(44, 62, 80, 0.22);
        border-color: rgba(44, 62, 80, 0.4);
      }
    }

    .title-accent-line {
      width: 58px;
    }
  }

  /* Translucent Glass Information Panel */
  .info-glass-panel {
    position: relative;
    z-index: 2;
    background: var(--glass-bg);
    border: 1px solid var(--glass-border);
    backdrop-filter: blur(8px);
    border-radius: 14px;
    padding: 12px 14px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    box-shadow: inset 0 1px 1px rgba(255, 255, 255, 0.15),
      0 4px 14px rgba(0, 0, 0, 0.06);

    .meta-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;

      &.status-meta-row {
        margin-bottom: 2px;
      }
    }

    .meta-left {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .meta-label {
      font-size: 0.72rem;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: var(--text-muted);
      display: flex;
      align-items: center;

      &.label-dark {
        color: rgba(44, 62, 80, 0.8);
      }
    }

    .meta-val {
      font-size: 0.86rem;
      font-weight: 700;
      color: var(--text-main);

      &.val-dark {
        color: #2c3e50;
      }
    }

    .status-chip-pill {
      font-weight: 800 !important;
      letter-spacing: 0.02em !important;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);

      .status-dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: #ffffff;
        margin-right: 6px;
        box-shadow: 0 0 4px #ffffff;
      }
    }

    .download-btn-icon {
      background: rgba(255, 255, 255, 0.18) !important;
      backdrop-filter: blur(4px);
      border: 1px solid rgba(255, 255, 255, 0.3) !important;
      transition: transform 0.25s ease, background 0.25s ease !important;

      &:hover {
        transform: translateY(-2px) scale(1.08);
        background: rgba(255, 255, 255, 0.32) !important;
      }
    }
  }

  .phase-2 .info-glass-panel .download-btn-icon {
    background: rgba(44, 62, 80, 0.12) !important;
    border: 1px solid rgba(44, 62, 80, 0.25) !important;

    &:hover {
      background: rgba(44, 62, 80, 0.22) !important;
    }
  }

  /* Card Action Bar */
  .card-action-bar {
    position: relative;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: flex-start;

    .action-link {
      text-decoration: none;
      display: inline-block;
      width: 100%;
    }

    .card-action-btn {
      position: relative;
      overflow: hidden;
      width: 100%;
      height: 40px !important;
      border-radius: 12px !important;
      font-weight: 800 !important;
      letter-spacing: 0.04em !important;
      text-transform: capitalize !important;
      box-shadow: 0 4px 14px rgba(0, 0, 0, 0.16) !important;
      transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1),
        box-shadow 0.3s ease !important;

      .action-btn-arrow {
        transition: transform 0.3s ease;
      }

      /* Button Shine Sweep */
      &::after {
        content: "";
        position: absolute;
        top: 0;
        left: -100%;
        width: 60%;
        height: 100%;
        background: linear-gradient(
          90deg,
          transparent,
          rgba(255, 255, 255, 0.35),
          transparent
        );
        transform: skewX(-20deg);
        transition: left 0.75s ease;
      }

      &.primary-action {
        background: rgba(0, 0, 0, 0.24) !important;
        color: #ffffff !important;
        border: 1px solid rgba(255, 255, 255, 0.3) !important;
        backdrop-filter: blur(6px);
      }

      &.secondary-action {
        background: rgba(255, 255, 255, 0.25) !important;
        color: #ffffff !important;
        border: 1px solid rgba(255, 255, 255, 0.4) !important;
        backdrop-filter: blur(6px);
      }

      &.dark-btn {
        background: #1e293b !important;
        color: #ffffff !important;
        border: 1px solid rgba(255, 255, 255, 0.2) !important;
      }

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25) !important;

        .action-btn-arrow {
          transform: translateX(4px);
        }

        &::after {
          left: 140%;
        }
      }
    }
  }
}

/* Keyframe Animations */
@keyframes ambientOrbFloat {
  0% {
    transform: translate(0, 0) scale(1);
  }
  100% {
    transform: translate(30px, -40px) scale(1.08);
  }
}

@keyframes headerShimmer {
  0% {
    left: -100%;
  }
  25% {
    left: 140%;
  }
  100% {
    left: 140%;
  }
}

@keyframes cardEntrance {
  0% {
    opacity: 0;
    transform: translateY(20px) scale(0.98);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes badgeFloat {
  0% {
    transform: translateY(0);
  }
  100% {
    transform: translateY(-6px);
  }
}

@keyframes ringRotate {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Micro animations for hero icons */
.icon-animate-pulse {
  animation: microPulse 3s ease-in-out infinite alternate;
}
.icon-animate-spin {
  animation: microSpin 16s linear infinite;
}
.icon-animate-float {
  animation: badgeFloat 3.5s ease-in-out infinite alternate;
}
.icon-animate-sparkle {
  animation: microSparkle 2.5s ease-in-out infinite alternate;
}
.icon-animate-glow {
  animation: microGlow 3s ease-in-out infinite alternate;
}

@keyframes microPulse {
  0% {
    transform: scale(1);
  }
  100% {
    transform: scale(1.06);
  }
}
@keyframes microSpin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
@keyframes microSparkle {
  0% {
    opacity: 0.85;
    transform: scale(1);
  }
  100% {
    opacity: 1;
    transform: scale(1.08) rotate(5deg);
  }
}
@keyframes microGlow {
  0% {
    box-shadow: inset 0 1px 2px rgba(255, 255, 255, 0.25),
      0 4px 12px rgba(0, 0, 0, 0.1);
  }
  100% {
    box-shadow: inset 0 1px 2px rgba(255, 255, 255, 0.4),
      0 6px 18px rgba(255, 255, 255, 0.35);
  }
}

/* Responsive Media Queries */
@media (max-width: 1280px) {
  #mission-onelakh {
    padding: 16px 20px;

    .phase-dashboard-grid {
      gap: 16px;
    }

    .phase-card {
      padding: 16px 18px 18px 18px;
    }

    .achievement-medallion {
      width: 82px;
      height: 82px;

      .badge-img-wrapper {
        width: 68px;
        height: 68px;
      }
    }
  }
}

@media (max-width: 960px) {
  #mission-onelakh {
    .phase-dashboard-grid {
      grid-template-columns: repeat(2, 1fr);
      gap: 16px;
    }
  }
}

@media (max-width: 640px) {
  #mission-onelakh {
    padding: 12px 14px 28px 14px;

    .executive-header-banner {
      padding: 14px 16px;
      margin-bottom: 16px;

      .header-titles .page-title-text {
        font-size: 1.15rem;
      }

      .location-badge {
        font-size: 0.78rem;
      }
    }

    .phase-dashboard-grid {
      grid-template-columns: 1fr;
      gap: 14px;
    }

    .phase-card {
      padding: 16px;
    }

    .achievement-medallion {
      width: 74px;
      height: 74px;

      .badge-img-wrapper {
        width: 60px;
        height: 60px;
      }
    }

    .card-hero-row {
      .phase-title-text {
        font-size: 1.05rem;
      }
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  #mission-onelakh {
    .phase-card-wrapper,
    .badge-orbit,
    .badge-ring,
    .badge-img-wrapper,
    .icon-animate-pulse,
    .icon-animate-spin,
    .icon-animate-float,
    .icon-animate-sparkle,
    .icon-animate-glow,
    .header-light-sweep {
      animation: none !important;
      transition: none !important;
    }
  }
}
</style>